import React, { memo } from "react"
import { Handle, Position, NodeProps } from "@xyflow/react"
import {
  CHECK_KIND_GROUP,
  CHECK_KIND_LEAF,
  CheckExpression,
  ConditionNodeData,
  LeafCheck,
  OPERATOR_AND
} from "lib/types/workflow"
import { CHECK_DESCRIPTORS } from "components/Workflow/NodeEditor/conditionSchema"
import { OTHERWISE, THEN } from "../../branchKind"

import {
  NodeContainer,
  NodeHeader,
  NodeBody,
  CheckInfo
} from "./ConditionNode.styled"
import {
  StatusBadge,
  FailedMessageBlock,
  statusIcon
} from "./ExecutionOverlay.styled"
import TerminalAddButton from "./TerminalAddButton"

const countLeaves = (expr: CheckExpression): number =>
  expr.kind === CHECK_KIND_LEAF
    ? 1
    : expr.children.reduce((n, c) => n + countLeaves(c), 0)

const summariseLeaf = (check: LeafCheck): string => {
  const label = CHECK_DESCRIPTORS[check.type]?.label ?? check.type
  if (check.type === "CUSTOM_HTTP") {
    const ep = check.endpoint ?? ""
    const trimmed = ep.length > 30 ? `${ep.substring(0, 30)}...` : ep || "—"
    return `HTTP: ${trimmed}`
  }
  if (check.type === "ORDER_DATE") {
    const n = check.values?.length ?? 0
    return `${label} ${check.comparison.toLowerCase()} (${n})`
  }
  if (
    check.type === "ORDER_LINES" ||
    check.type === "SHIPPING_FEES" ||
    check.type === "GIFTS"
  ) {
    const c = check.count?.length
    if (!c) return label
    return `${label} length ${c.comparison.toLowerCase()} ${c.values.join(", ")}`
  }
  // Field-bag — list how many fields are set.
  const fields =
    ((check as unknown as { fields?: Record<string, unknown> }).fields) ?? {}
  const setFieldCount = Object.keys(fields).length
  return `${label} (${setFieldCount} ${setFieldCount === 1 ? "field" : "fields"})`
}

const getCheckInfoText = (d: ConditionNodeData): string => {
  const expr = d.expression
  if (!expr) return "—"
  if (expr.kind === CHECK_KIND_GROUP) {
    const total = countLeaves(expr)
    const operatorLabel = expr.operator === OPERATOR_AND ? "All of" : "Any of"
    return `${operatorLabel} ${total} ${total === 1 ? "check" : "checks"}`
  }
  return summariseLeaf(expr.check)
}

const ConditionNode = memo(({ id, data }: NodeProps) => {
  const d = data as ConditionNodeData
  const terminals = d._terminalHandles ?? []
  const status = d._executionStatus
  return (
    <NodeContainer $skipped={status === "skipped"}>
      <Handle type="target" position={Position.Top} id="input" />
      <NodeHeader>Condition</NodeHeader>
      <NodeBody>{d.label}</NodeBody>
      <CheckInfo>{getCheckInfoText(d)}</CheckInfo>
      {d._failedMessage && (
        <FailedMessageBlock>{d._failedMessage}</FailedMessageBlock>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        id="true-output"
        style={{ left: "25%" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false-output"
        style={{ left: "75%" }}
      />
      {terminals.includes("true-output") && d._onAddAtTerminal && (
        <TerminalAddButton
          left="25%"
          branchLabel={THEN}
          onClick={() => d._onAddAtTerminal?.(id, "true-output")}
          highlighted={d._activeTerminalDropHandle === "true-output"}
        />
      )}
      {terminals.includes("false-output") && d._onAddAtTerminal && (
        <TerminalAddButton
          left="75%"
          branchLabel={OTHERWISE}
          onClick={() => d._onAddAtTerminal?.(id, "false-output")}
          highlighted={d._activeTerminalDropHandle === "false-output"}
        />
      )}
      {status && (
        <StatusBadge $status={status}>{statusIcon(status)}</StatusBadge>
      )}
    </NodeContainer>
  )
})

ConditionNode.displayName = "ConditionNode"

export default ConditionNode
